// Copyright (C) Microsoft Corporation. All rights reserved.
import type {
  ExtensionPrompt,
  Group,
  Prompt,
  UserPrompt,
  WorkgroupMicrosoftPrompt,
  WorkgroupUserPrompt,
} from "@1js/pl-types";
export type {
  ExtensionPrompt,
  Group,
  HydratedEntity,
  MicrosoftPrompt,
  Prompt,
  PromptCategory,
  PromptRingType,
  UserPrompt,
} from "@1js/pl-types";

export type Appearance =
  | "in-app"
  | "in-app-v2"
  | "web"
  | "web-v2"
  | "m365"
  | "win-copilot"
  | "single-col"
  | "bing-mobile" // deprecated, it now just uses same style as 'single-col'. Remove this once <PromptLibraryDialog> from BizChat is using the now more simplified appearance.
  | "bing-web" // deprecated, it now just uses same style as 'm365'. Remove this once <PromptLibraryDialog> from BizChat is using the now more simplified appearance.
  | "underside"; // deprecated, it now just uses same style as 'single-col'. Remove this once <PromptLibraryDialog> from BizChat is using the now more simplified appearance.

export const ProductTypes = [
  "Word",
  "PowerPoint",
  "Outlook",
  "Teams",
  "BizChat",
  "Copilot",
  "Excel",
  "OneNote",
  "OneDrive",
  "Loop",
  "Whiteboard",
  "SharePoint",
  "Topics",
  "Forms",
  "Planner",
  "Stream",
  "VivaEngage",
  "VivaGoals",
  "AzureWeb",
  "Azure",
  "CWC",
] as const;

export type ProductType = (typeof ProductTypes)[number];

export const JobTypes = ["Department", "Industry", "Education"] as const;
export type JobType = (typeof JobTypes)[number];

export type Product = {
  name: ProductType;
  surface?: string[];
};

export type PromptStatistics = {
  sharedCount: number;
  savedCount: number;
};

export type HeroData = {
  enabled: boolean;
  rank: number;
  altText: string;
  image: string;
};

export type PromptTier = "Work" | "Personal";

export type FacetDictionary = {
  [key: string]: FacetValue[];
};

export type FacetValue = {
  /**
   * Displayed value.
   */
  value: string;

  count?: number;

  /**
   * Internal Id for mapping.
   */
  id?: string;
};

// A custom weighting function on product types so we can sort product type in a certain order.
export const ProductWeights: Record<ProductType, number> = {
  BizChat: 1,
  CWC: 2,
  Teams: 3,
  Outlook: 4,
  Word: 5,
  Excel: 6,
  PowerPoint: 7,
  OneNote: 8,
  Loop: 9,
  Whiteboard: 10,
  SharePoint: 11,
  OneDrive: 12,
  Planner: 13,
  Stream: 14,
  Forms: 15,
  VivaEngage: 16,
  VivaGoals: 17,
  Azure: 18,
  Copilot: 19,
  Topics: 20,
  AzureWeb: 21,
};

export const JobTypeWeights: Record<JobType, number> = {
  Department: 1,
  Industry: 2,
  Education: 3,
};

export const mockExtensionIconMap: Record<string, string> = {
  "U_82cef8a7-6f2c-3aea-2da1-3b0668a91c54.klarnaGPT":
    "data:image/png;base64,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",
  "53a53045-2e5e-4cfc-8bc3-14acd2bc824f":
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAR3SURBVHhe7dt/aFVlHMfxj/du5tYi+vVnNTWmQlFgElTLtHLqGJI6CH9s0mS5Ag3ZMMsFpSK2IebKVrpsKjIzkxlbWipBo/1jEPaHU2pZ0T9pWbmm4e697Tn3e+daE+7uvef5nHP2ff2z5znKhfu8Oec899eYSz29MSiakPxVJBqATAOQaQAyDUCmAcg0AJkGINMAZBqATAOQaQAyDUCmAcg0AJkGINMAZBqATAOQaQAyDUCmAcg0AFmgvxd09WofGne14LPjHTj30y+YmH8X5sx6DMvLFiIcDsv/4gpsgPMXfsOyqpfRdfZ7OXLN/fdOxs63NuLWW26WIzyBDGAWf1FFNbrP/SxH/m9ywQTsea+OHiFw94Chi5+VFcara15A57EWvLS6EqFQ/Cl3ne3G0soa/H7xT2fOEqgzYLjF31ZXi6KZjzpzo7XtOKrXbUY0GnXm7DMhMGdAMotvzCt+AvUb1njmTLB+BsRiMby/5yAOt5/Ame+6nZ1KsnJzxqHw4WmYNfMRlMyZMbCTSXbxB/PKmWA1gFn8ZVVr0dF5Uo6krnzR0861PZXFT/BCBKsBdjYfwKYt78osfWtXP4f9H7entPgJ7AhWAxSXVjrXXGN5eSlWrShDbm6OM09GT08v6hqasLelVY5ck8riJzAjWAtgntykqbOdv+YGeKrzE+SMu0H+NXkX//gLD06fL7O4dBY/YWiEB+6bggO73xy4WbvF2i4oGo0NPLlwOJTS4hs35d0oo7hMLL4xdHf0zbennc2C23y/Da2teT7txU8wEapXVsgMOHLsSxm5x/cB5hY9LqPMKB70eD/8GL+5u8n3AdqPfiGjzGgb9Hjj775TRu7xfYD1ddtx9ESHzNJjbsT125pkBsx+slBG7vF9gL6+CFbWrE87wnC7oGeXLnDGbvJ9ACPdCMO9DtjRsMH1Lajh6wBmgSbkx6/TqUZgvggzfB3AvJ7Y11SfcgT24hu+vwTdcfttKUXwwuIbgbgHjDSCVxbfCEQAI9kIXlp8IzABjOtFaN53CL+ev4AdzR96avENa++GmsWYNLVIZt7wSvWK/r3+QplxBOoMGKmN9Y14bfPbiEQicsS+UR3A2N1/efr0c/ff9bweyiUoOzsLXSePOOORysTjDP1kbW7RdDS8UeuMbRuVZ0BeXi5erCqXGdDx1dcysm/UXoIGf7J2+coVGdk36u8BbBqATAOQaQAyDUCmAcg0AJkGINMAZBqAzFqAUGjMwNc8IpFo/8v/f5wxy6Wev2Vk3tTLlpF9FgOEUHBPvjM2n0ht3f4BensvO3PbzLuhW99plpn5ZGy8jOyz/AuZj7BpS6PMvOP1dauwuLREZnZZvQdUlC3AjMKHZOYN5qvtrMU3rJ4Bhvmh3q69B9HaNvJfSWbK2LHZmFIwEfNLnsKSZ+bJUQ7rAdR/6TaUTAOQaQAyDUCmAcg0AJkGINMAZBqATAOQaQAyDUCmAcg0AJkGINMAZBqATAOQaQAyDUCmAcg0AJkGINMAZBqATANQAf8CedccD7q6MJIAAAAASUVORK5CYII=",
};

export const MockExtensionPrompts: ExtensionPrompt[] = [
  {
    Id: "gpt_U_82cef8a7-6f2c-3aea-2da1-3b0668a91c54.klarnaGPT_llCtaXtBD1luP+9DDk/hjbcZGVhR9KQsHj3ECu3timk=_en-us",
    CommandText: "Find Toothbrush",
    DisplayDescription: "Find Toothbrush",
    Title: "Find Toothbrush",
    DisplayCategory: "Ask",
    DisplayText: "Find Toothbrush",
    PromptId:
      "U_82cef8a7-6f2c-3aea-2da1-3b0668a91c54.klarnaGPT_llCtaXtBD1luP+9DDk/hjbcZGVhR9KQsHj3ECu3timk=",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
      },
    },
    Origin: "GPT",
    ProviderId: "U_82cef8a7-6f2c-3aea-2da1-3b0668a91c54.klarnaGPT",
    ProviderName: "Klarna GPT",
    ProviderPartyType: "ThirdParty",
    Text: "Find Toothbrush",
    ReferenceId: "036f632b-b58e-d4a5-3a6d-ce4eb8730754.1000.1",
    Products: ["BizChat"],
    DeveloperName: "Contoso",
  },
  {
    Id: "3P_53a53045-2e5e-4cfc-8bc3-14acd2bc824fcreateSunnyBotItem_3kmhVkwHNISh7KgOmOrwnDW12F4/EvkfB0SSJSKxsR0=_en-us",
    CommandText: "Create a SunnyBot on SigninCard and assign to my Manager",
    DisplayDescription:
      "Create a SunnyBot on SigninCard and assign to my Manager",
    Title: "Create a SunnyBot on SigninCard and assign to my Manager",
    DisplayCategory: "Ask",
    DisplayText: "Create a SunnyBot on SigninCard and assign to my Manager",
    PromptId:
      "53a53045-2e5e-4cfc-8bc3-14acd2bc824fcreateSunnyBotItem_3kmhVkwHNISh7KgOmOrwnDW12F4/EvkfB0SSJSKxsR0=",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
      },
    },
    Origin: "Plugin",
    ProviderId: "53a53045-2e5e-4cfc-8bc3-14acd2bc824f",
    ProviderName: "SunnyBot",
    ProviderPartyType: "ThirdParty",
    Text: "Create a SunnyBot on SigninCard and assign to my Manager",
    ReferenceId: "806f8a62-b258-af7c-d31f-0f8adc73a1d9.2000.12",
    Products: ["BizChat"],
    DeveloperName: "Contoso",
  },
];

export const MockPrompts: Prompt[] = [
  {
    Id: "b66be088-70ff-4b75-b8ad-21614941aae0_en-us",
    PromptId: "b66be088-70ff-4b75-b8ad-21614941aae0",
    Title: "Share meeting notes",
    DisplayText:
      "Draft an email with notes and action items from <entity type='meeting'>meeting</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Draft an email with notes and action items from <entity type='meeting'>meeting</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "BizChatPri", Value: "1" }],
  },
  {
    Id: "95024772-2835-4ed3-991f-e1bc040a7b30_en-us",
    PromptId: "95024772-2835-4ed3-991f-e1bc040a7b30",
    Title: "Write a status update",
    DisplayText:
      "Write a status report to my manager based on <entity type='file'>file</entity>. Include any upcoming deliverables, due dates, and key decisions",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText:
      "Write a status report to my manager based on <entity type='file'>file</entity>. Include any upcoming deliverables, due dates, and key decisions.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Financial Services" }],
  },
  {
    Id: "9234a086-5ac9-4cc8-8a47-27b8b7631b67_en-us",
    PromptId: "9234a086-5ac9-4cc8-8a47-27b8b7631b67",
    Title: "Catch up on messages",
    DisplayText: "Summarize Teams messages from this week about...",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText: "Summarize Teams messages from this week about...",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Financial Services" }],
  },
  {
    Id: "e34b9f77-2653-4264-a133-6582cb97c1a1_en-us",
    PromptId: "e34b9f77-2653-4264-a133-6582cb97c1a1",
    Title: "How to",
    DisplayText: "How do I write a request for proposal?",
    DisplayDescription: "",
    DisplayCategory: "Ask",
    CommandText: "How do I write a request for proposal?",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Retail" }],
  },
  {
    Id: "7d93e1a8-5813-4c8e-abf9-92adf09564b2_en-us",
    PromptId: "7d93e1a8-5813-4c8e-abf9-92adf09564b2",
    Title: "Help me write",
    DisplayText:
      "Write an email to my team about our top priorities for next quarter from <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Write an email to my team about our top priorities for next quarter from <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Manufacturing" }],
  },
  {
    Id: "892b000a-6c7b-4399-8792-c96a949d2cca_en-us",
    PromptId: "892b000a-6c7b-4399-8792-c96a949d2cca",
    Title: "Discover a company topic",
    DisplayText:
      "I need to explain [concept] to a [role/target audience]. I need to include an explainer of what it is, how it works, and a",
    DisplayDescription:
      "\\u003Cb\\u003EExample\\u003C/b\\u003E\\u003Cbr\\u003E\\u0026quot;I need to explain  \\u003Cb\\u003EExpense Management\\u003C/b\\u003E to a \\u003Cb\\u003Enew accountant intern\\u003C/b\\u003E, including an explanation of what it is, how it works, and the value proposition. Create a script that includes two analogies to help explain it to someone new to the concept.\\u0026quot;\\u003Cbr\\u003E\\u003Cbr\\u003E",
    DisplayCategory: "Create",
    CommandText:
      "I need to explain [concept] to a [role/target audience]. I need to include an explainer of what it is, how it works, and a value proposition associated with it. Create a script I could use. Include 2 analogies to help it make sense to someone new to this concept.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Manufacturing" }],
  },
  {
    Id: "56eb2c14-2988-45fe-aa24-9b90d24328d8_en-us",
    PromptId: "56eb2c14-2988-45fe-aa24-9b90d24328d8",
    Title: "Draft an FAQ",
    DisplayText: "Create an FAQ based on <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText: "Create an FAQ based on <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Industry", Value: "Manufacturing" }],
  },
  {
    Id: "674f6995-90f4-4ff8-b127-b3dd754a68b6_en-us",
    PromptId: "674f6995-90f4-4ff8-b127-b3dd754a68b6",
    Title: "Get calendar info",
    DisplayText:
      "When's my next meeting with <entity type='person'>person</entity>?",
    DisplayDescription: "",
    DisplayCategory: "Ask",
    CommandText:
      "When's my next meeting with <entity type='person'>person</entity>?",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Human Resources" }],
  },
  {
    Id: "94ffeade-968d-4009-b9ed-509ae8fc9154_en-us",
    PromptId: "94ffeade-968d-4009-b9ed-509ae8fc9154",
    Title: "Create a quiz ",
    DisplayText:
      "Write a practice quiz about [Topic]. Include at least 20 questions, and group questions by category. Provide a detailed",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Write a practice quiz about [Topic]. Include at least 20 questions, and group questions by category. Provide a detailed answer for each question.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Human Resources" }],
  },
  {
    Id: "aff7c8b9-89e6-4026-8da4-b5a1f4e736c6_en-us",
    PromptId: "aff7c8b9-89e6-4026-8da4-b5a1f4e736c6",
    Title: "Create course list",
    DisplayText:
      "Create 5 short courses for business leaders based on <placeholder>upload your file</placeholder>.",
    DisplayDescription:
      "Create 5 short courses for business leaders based on <placeholder>upload your file</placeholder>.",
    DisplayCategory: "Create",
    CommandText:
      "Create 5 short courses for business leaders based on <placeholder>upload your file</placeholder>.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Marketing" }],
  },
  {
    Id: "6f055863-e85d-4638-9086-a79eea53908f_en-us",
    PromptId: "6f055863-e85d-4638-9086-a79eea53908f",
    Title: "Prep for a meeting",
    DisplayText:
      "I've got an upcoming meeting with <entity type='person'>person<entity>. Based on my recent communication and recent news items, create a bulleted list.<placeholder></placeholder>",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText:
      "I've got an upcoming meeting with <entity type='person'>person<entity>. Based on my recent communication and recent news items, create a bulleted list of things to know ahead of this meeting.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "Department", Value: "Sad PM" }],
  },
  {
    Id: "6bf6bdda-d865-41e7-ab15-efb9bc0645db_en-us",
    PromptId: "6bf6bdda-d865-41e7-ab15-efb9bc0645db",
    Title: "Get key info",
    DisplayText: "List key points from <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Catch up",
    CommandText: "List key points from <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "0101b4e6-15b5-464b-abfa-d2dc648bf0b5_en-us",
    PromptId: "0101b4e6-15b5-464b-abfa-d2dc648bf0b5",
    Title: "Brainstorm",
    DisplayText:
      "Suggest 10 compelling taglines based on <entity type='file'>file</entity>",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText:
      "Suggest 10 compelling taglines based on <entity type='file'>file</entity>",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "eb65855b-5eaf-4184-8a54-c2301e023ecd_en-us",
    PromptId: "eb65855b-5eaf-4184-8a54-c2301e023ecd",
    Title: "Draft an email template",
    DisplayText:
      "Draft a personal email I can send to people in [role] to call attention to recent [Product] news. Use <entity type='meeting'>meeting</entity> to add context.",
    DisplayDescription:
      "\\u003Cb\\u003EExample\\u003C/b\\u003E\\u003Cbr\\u003E\\u0026quot;Draft a personal email I can send to people in \\u003Cb\\u003ESales\\u003C/b\\u003E to call attention to recent \\u003Cb\\u003Eelectric bike\\u003C/b\\u003E news. Use \\u003Cu\\u003EWeekly_product_review\\u003C/u\\u003E to add context.\\u0026quot;\\u003Cbr\\u003E\\u003Cbr\\u003E",
    DisplayCategory: "Create",
    CommandText:
      "Draft a personal email I can send to people in [role] to call attention to recent [Product] news. Use <entity type='meeting'>meeting</entity> to add context.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "0110c811-9f0d-486d-b884-ef265111218e_en-us",
    PromptId: "0110c811-9f0d-486d-b884-ef265111218e",
    Title: "Recently asked questions",
    DisplayText:
      "Look for emails about [topic] over the last two weeks. Find common themes and organize them into a list.",
    DisplayDescription:
      "\\u003Cb\\u003EExample\\u003C/b\\u003E\\u003Cbr\\u003E\\u0026quot;Look for emails about \\u003Cb\\u003Eour 2024 company growth strategy\\u003C/b\\u003E over the last two weeks. Find common themes and organize them into a list.\\u0026quot;\\u003Cbr\\u003E\\u003Cbr\\u003E",
    DisplayCategory: "Understand",
    CommandText:
      "Look for emails about [topic] over the last two weeks. Find common themes and organize them into a list.",
    Products: ["BizChat"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
  },
  {
    Id: "96359aba-ce76-4496-afcb-60ac13ad3b78_en-us",
    PromptId: "96359aba-ce76-4496-afcb-60ac13ad3b78",
    Title: "Code a binary search in Python",
    DisplayText: "Write a Python script to perform binary search",
    DisplayDescription: "",
    DisplayCategory: "Code",
    CommandText: "Write a Python script to perform binary search",
    Products: ["CWC"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "BizChatPri", Value: "1" }],
  },
];

export const MockSavedPrompts: UserPrompt[] = [
  // User authored from Production site, edited user details
  {
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Id: "4788d584-1eaa-4a18-8f9c-459950cfcf01",
    CommandText: "How to write a proposal?",
    Title: "Saved prompt",
    DisplayCategory: "Created by Me",
    DisplayText: "How to write a proposal?",
    Products: ["BizChat"],
    PromptId: "4788d584-1eaa-4a18-8f9c-459950cfcf01",
    Author: {
      DisplayName: "Daniela Mandera",
      Email: "daniela@contoso.com",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
    LogicalId: "5c97425e-d593-4fec-ae96-31c82579e874",
    ReferenceId: null,
    Origin: "User",
    CreatedDateTimeInUtc: "2024-03-01T18:22:03+00:00",
    LastUpdatedDateTimeInUtc: "2024-03-01T18:22:04+00:00",
  },
  {
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Id: "6788d584-1eaa-4a18-8f9c-459950cfcf01",
    CommandText: "What should I prepare for a design review?",
    Title: "Be prepared",
    DisplayCategory: "Created by Me",
    DisplayText: "What should I prepare for a design review?",
    Products: ["BizChat"],
    PromptId: "6788d584-1eaa-4a18-8f9c-459950cfcf01",
    Author: {
      DisplayName: "Daniela Mandera",
      Email: "daniela@contoso.com",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
    LogicalId: "6c97425e-d593-4fec-ae96-31c82579e874",
    ReferenceId: null,
    Origin: "User",
    CreatedDateTimeInUtc: "2024-04-01T18:22:03+00:00",
    LastUpdatedDateTimeInUtc: "2024-04-01T18:22:04+00:00",
  },
  // Microsoft authored from Production site, edited user details
  {
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Id: "e3965690-49af-462c-b308-3d603275e2a9",
    CommandText:
      "What emails should be on my radar? Focus on those that seem urgent, important or are from my manager.",
    Title: "Microsoft authored",
    DisplayCategory: "Created by Me",
    DisplayText:
      "What emails should be on my radar? Focus on those that seem urgent, important or are from my manager.",
    Products: ["BizChat"],
    PromptId: "e3965690-49af-462c-b308-3d603275e2a9",
    Author: {
      DisplayName: "Daniela Mandera",
      Email: "daniela@contoso.com",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
    LogicalId: "7b1b3d1d-7ba4-44f8-89c2-186eb1ad16bf",
    ReferenceId: null,
    Origin: "User",
    CreatedDateTimeInUtc: "2024-04-19T07:37:25+00:00",
    LastUpdatedDateTimeInUtc: "2024-04-19T07:37:25+00:00",
  },
];

export const MockMemberGroups: Group[] = [
  {
    Id: "123",
    DisplayName: "Some group name 1",
    IsMember: true,
  },
  {
    Id: "1234",
    DisplayName: "Some group name 2",
    IsMember: true,
  },
  {
    Id: "123456",
    DisplayName: "Some group name 3",
    IsMember: true,
  },
  {
    Id: "123432",
    DisplayName: "Some group name 4",
    IsMember: true,
  },
];

export const MockHasPromptsGroups: Group[] = [
  {
    Id: "123",
    DisplayName: "Some group name 1",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "1234",
    DisplayName: "Some group name 2",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "123456",
    DisplayName: "Some group name 3",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "123432",
    DisplayName: "Some group name 4",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2001",
    DisplayName: "X Empty group 2001",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2002",
    DisplayName: "X Empty group 2002",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2003",
    DisplayName: "X Empty group 2003",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2004",
    DisplayName: "X Empty group 2004",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2005",
    DisplayName: "X Empty group 2005",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2006",
    DisplayName: "X Empty group 2006",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2007",
    DisplayName: "X Empty group 2007",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2008",
    DisplayName: "X Empty group 2008",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2009",
    DisplayName: "X Empty group 2009",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2010",
    DisplayName: "X Empty group 2010",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2011",
    DisplayName: "X Empty group 2011",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2012",
    DisplayName: "X Empty group 2012",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2013",
    DisplayName: "X Empty group 2013",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2014",
    DisplayName: "X Empty group 2014",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2015",
    DisplayName: "X Empty group 2015",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2016",
    DisplayName: "X Empty group 2016",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2017",
    DisplayName: "X Empty group 2017",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2018",
    DisplayName: "X Empty group 2018",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2019",
    DisplayName: "X Empty group 2019",
    HasPrompts: true,
    IsMember: true,
  },
  {
    Id: "2020",
    DisplayName: "X Empty group 2020",
    HasPrompts: true,
    IsMember: true,
  },
];

export const MockGroups: Group[] = [
  {
    Id: "123",
    DisplayName: "Some group name 1",
    IsMember: true,
  },
  {
    Id: "1234",
    DisplayName: "Some group name 2",
    IsMember: true,
  },
  {
    Id: "123456",
    DisplayName: "Some group name 3",
    IsMember: true,
  },
  {
    Id: "123432",
    DisplayName: "Some group name 4",
    IsMember: true,
  },
];

const group1Prompts: (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[] = [
  {
    Publisher: {
      Name: "John Doe",
      Address: "john.doe@contoso.com",
    },
    Slug: "suggest-new-content-a9008137-13c4-4131-9ccb-d1d13b28524c",

    Id: "a9008137-13c4-4131-9ccb-d1d13b28524c_en-us",
    PromptId: "a9008137-13c4-4131-9ccb-d1d13b28524c",
    Title: "Suggest new content",
    DisplayText:
      "Write about the benefits of <placeholder>focused work</placeholder>",
    DisplayDescription:
      "<b>Take it to the next level</b><br>Give Copilot more context about how you want to use the document. For example: Write an article for our company newsletter about the benefits of focused work. Keep the tone informal, and include examples whenever possible.<br><br><b>More to try</b><br>If the draft Copilot creates isn\\u0027t quite long enough, you can follow up by asking it to write more about the topic.",
    DisplayCategory: "Create",
    CommandText: "Write about the benefits of",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write about the benefits of <placeholder>focused work</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Publisher: {
      Name: "John Doe",
      Address: "john.doe@contoso.com",
    },
    Slug: "write-cover-letter-4a90f90d-1805-4f5b-8091-b0215dbb4b35",

    Id: "4a90f90d-1805-4f5b-8091-b0215dbb4b35_en-us",
    PromptId: "4a90f90d-1805-4f5b-8091-b0215dbb4b35",
    Title: "Write cover letter",
    DisplayText:
      "Write a cover letter for <placeholder>a tour guide job</placeholder>.",
    DisplayDescription:
      "<b>Take it to the next level</b><br>The more details you can give it, the more accurate the draft will be.",
    DisplayCategory: "Create",
    CommandText: "Write a cover letter for",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a cover letter for <placeholder>a tour guide job</placeholder>.",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
];

const group2Prompts: (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[] = [
  {
    Publisher: {
      Name: "Charlie Doe",
      Address: "charlie.doe@contoso.com",
    },
    Slug: "write-a-marketing-plan-e52cd2bc-0fb3-4233-a534-e8368b5f5b63",

    Id: "e52cd2bc-0fb3-4233-a534-e8368b5f5b63_en-us",
    PromptId: "e52cd2bc-0fb3-4233-a534-e8368b5f5b63",
    Title: "Write a marketing plan",
    DisplayText:
      "Write a 1 page marketing plan for <placeholder>a coffee shop</placeholder>.",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText: "Write a 1 page marketing plan for",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a 1 page marketing plan for <placeholder>a coffee shop</placeholder>.",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Publisher: {
      Name: "John Doe",
      Address: "john.doe@contoso.com",
    },
    Slug: "write-a-proposal-810dae88-7d9b-422a-8f24-a3bb07f71c49",

    Id: "810dae88-7d9b-422a-8f24-a3bb07f71c49_en-us",
    PromptId: "810dae88-7d9b-422a-8f24-a3bb07f71c49",
    Title: "Write a proposal",
    DisplayText:
      "Write a project proposal to <placeholder>convince my manager to allocate resources to a new initiative</placeholder>",
    DisplayDescription:
      "<b>Take it to the next level</b><br>Add as many additional details as you can about the project to help Copilot be more specific and accurate.",
    DisplayCategory: "Create",
    CommandText: "Write a project proposal to",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a project proposal to <placeholder>convince my manager to allocate resources to a new initiative</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Publisher: {
      Name: "Charlie Doe",
      Address: "Charlie.doe@contoso.com",
    },
    Slug: "draft-a-white-paper-619235ab-2c07-4412-8b77-717bda8a3bf1",

    Id: "619235ab-2c07-4412-8b77-717bda8a3bf1_en-us",
    PromptId: "619235ab-2c07-4412-8b77-717bda8a3bf1",
    Title: "Draft a white paper",
    DisplayText:
      "Write a short white paper about <placeholder>design thinking</placeholder>.",
    DisplayDescription: "",
    DisplayCategory: "Create",
    CommandText: "Write a short white paper about",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: true,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Write a short white paper about <placeholder>design thinking</placeholder>.",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
];

const group3Prompts: (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[] = [
  {
    Publisher: {
      Name: "John Doe",
      Address: "john.doe@contoso.com",
    },
    Slug: "highlight-mentions-e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",

    Id: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1_en-us",
    PromptId: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",
    Title: "Highlight mentions",
    DisplayText:
      "Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    DisplayDescription: "",
    DisplayCategory: "Edit",
    CommandText: "Highlight in yellow all mentions of",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
  {
    Publisher: {
      Name: "Charlie Doe",
      Address: "charlie.doe@contoso.com",
    },
    Slug: "summarize-f6f399bc-db16-40ab-986b-5fa9fe239f0f",

    Id: "f6f399bc-db16-40ab-986b-5fa9fe239f0f_en-us",
    PromptId: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    Title: "Summarize",
    DisplayText: "Summarize as a <placeholder>status email</placeholder>",
    DisplayDescription:
      "<b>To be clear</b><br>If you\\u2019d like to narrow the scope of what OneNote summarizes just select the text you want summarized before running the prompt.<br><br><b>Take it to the next level</b><br>You can give Copilot more detail about how you want the status email. For example consider adding 'Use a professional tone.'",
    DisplayCategory: "Understand",
    CommandText: "Summarize as a",
    Products: ["OneNote"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Summarize as a <placeholder>status email</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
  },
];
const group4Prompts: (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[] = [
  // Microsoft authored prompt
  {
    Publisher: {
      Name: "John Doe",
      Address: "john.doe@contoso.com",
    },
    Slug: "highlight-mentions-e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",

    Id: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1_en-us",
    PromptId: "e601b8ab-2956-4f97-a5f0-5b6de33d0ea1",
    Title: "Highlight mentions",
    DisplayText:
      "Microsoft prompt Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    DisplayDescription: "",
    DisplayCategory: "Edit",
    CommandText: "Highlight in yellow all mentions of",
    Products: ["Word"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Text: "Highlight in yellow all mentions of <placeholder>company names</placeholder>",
    ReferenceId: "00000000-0000-0000-0000-000000000000",
    Origin: "Microsoft",
  },
  // Synthetic user prompt without user details
  {
    Publisher: {
      Name: "John Doe",
      Address: "john.doe@contoso.com",
    },
    Id: "f6f399bc-db16-40ab-986b-5fa9fe239f0f_en-us",
    PromptId: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    Title: "Summarize",
    DisplayText:
      "User prompt without user. Summarize as a <placeholder>status email</placeholder>",
    DisplayDescription:
      "<b>To be clear</b><br>If you\\u2019d like to narrow the scope of what OneNote summarizes just select the text you want summarized before running the prompt.<br><br><b>Take it to the next level</b><br>You can give Copilot more detail about how you want the status email. For example consider adding 'Use a professional tone.'",
    DisplayCategory: "Understand",
    CommandText: "Summarize as a",
    Products: ["OneNote"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    ReferenceId: "00000000-0000-0000-0000-000000000000",
    Origin: "User",
  },
  // Synthetic user prompt with user details
  {
    Publisher: {
      Name: "John Doe",
      Address: "john.doe@contoso.com",
    },
    Id: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    PromptId: "f6f399bc-db16-40ab-986b-5fa9fe239f0f",
    Title: "Summarize",
    DisplayText:
      "User prompt with user. Summarize as a <placeholder>status email</placeholder>",
    DisplayDescription:
      "<b>To be clear</b><br>If you\\u2019d like to narrow the scope of what OneNote summarizes just select the text you want summarized before running the prompt.<br><br><b>Take it to the next level</b><br>You can give Copilot more detail about how you want the status email. For example consider adding 'Use a professional tone.'",
    DisplayCategory: "Understand",
    CommandText: "Summarize as a",
    Products: ["OneNote"],
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    ReferenceId: "00000000-0000-0000-0000-000000000000",
    Origin: "User",
    Author: {
      DisplayName: "Daniela Mandera",
      Avatar:
        "https://fabricweb.azureedge.net/fabric-website/assets/images/avatar/KatriAthokas.jpg",
    },
  },
];

export const MockCWCPrompts: Prompt[] = [
  {
    Id: "96359aba-ce76-4496-afcb-60ac13ad3b78_en-us",
    PromptId: "96359aba-ce76-4496-afcb-60ac13ad3b78",
    Title: "Code a binary search in Python",
    DisplayText: "Write a Python script to perform binary search",
    DisplayDescription: "",
    DisplayCategory: "Code",
    CommandText: "Write a Python script to perform binary search",
    Products: ["CWC"],
    Text: "",
    ReferenceId: "",
    UserActivity: {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "0001-01-01T00:00:00+00:00",
      },
    },
    Metadata: [{ Name: "BizChatPri", Value: "1" }],
  },
];
export const MockGroupPrompts: Record<
  string,
  (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
> = {};
MockGroupPrompts[MockHasPromptsGroups[0].Id] = group1Prompts;
MockGroupPrompts[MockHasPromptsGroups[1].Id] = group2Prompts;
MockGroupPrompts[MockHasPromptsGroups[2].Id] = group3Prompts;
MockGroupPrompts[MockHasPromptsGroups[3].Id] = group4Prompts;
